import React, { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import {
  buildMissionControlStages,
  extractResearchSources,
  getStageFocus,
} from "@/lib/missionControlWorkflow";
import { PlatformPreviewPanel } from "@/components/content/PlatformPreviewPanel";

/**
 * Agent at Work — the run experience (design: docs/v3-mockups/agent-at-work.html).
 *
 * The agent moves through its phases, narrates each in plain language, and leaves
 * the real output on screen — then hands the finished proofs off for review. This
 * is the raw run: watch-only. Editing/regenerating/approving happen AFTER, in the
 * review surface below — not inside it.
 */

function elapsedLabel(ms) {
  if (!Number.isFinite(ms) || ms < 0) return "0.0s";
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${Math.round(s % 60)}s`;
}

function ThinkingDots() {
  return (
    <span className="ml-1 inline-flex items-center gap-1 align-middle" aria-hidden>
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="h-1 w-1 rounded-full bg-[color:var(--accent-cyan)]"
          style={{ animation: "aaw-blink 1s infinite", animationDelay: `${i * 0.18}s` }}
        />
      ))}
    </span>
  );
}

// pending | running | done | warning | skipped | failed
function dotFor(status) {
  if (status === "done") return { ring: "border-[color:var(--accent-green)] text-[color:var(--accent-green)] bg-[color:var(--accent-green)]/10", glyph: "✓" };
  if (status === "warning") return { ring: "border-[color:var(--accent-amber)] text-[color:var(--accent-amber)] bg-[color:var(--accent-amber)]/10", glyph: "!" };
  if (status === "failed") return { ring: "border-[color:var(--accent-red)] text-[color:var(--accent-red)] bg-[color:var(--accent-red)]/10", glyph: "×" };
  if (status === "running") return { ring: "border-[color:var(--accent-cyan)] text-[color:var(--accent-cyan)]", glyph: "" };
  if (status === "skipped") return { ring: "border-[color:var(--border-subtle)] text-[color:var(--text-3)]", glyph: "–" };
  return { ring: "border-[color:var(--border-subtle)] text-[color:var(--text-3)]", glyph: "" };
}

function confidenceMeta(label) {
  if (label === "High") return { w: "83%", cls: "text-[color:var(--accent-green)]" };
  if (label === "Medium") return { w: "58%", cls: "text-[color:var(--accent-amber)]" };
  if (label === "Low") return { w: "33%", cls: "text-[color:var(--text-3)]" };
  return { w: "50%", cls: "text-[color:var(--accent-cyan)]" };
}

function OutputBox({ children }) {
  return (
    <div className="mt-2.5 rounded-lg border p-3" style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-2)" }}>
      {children}
    </div>
  );
}

function OutputLabel({ children }) {
  return <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--text-3)]">{children}</p>;
}

// The real output the agent produced in this phase.
function PhaseOutput({ stage, run, assets, deliverables }) {
  const focus = getStageFocus(stage, run, assets);
  const sources = stage.id === "research" ? extractResearchSources(assets, run) : [];
  const subPhases = (stage.exactPhases || []).filter((p) => p.label);

  // Draft phase: the real native post previews it wrote (verbatim).
  if (stage.id === "draft" && deliverables?.length > 0) {
    return (
      <div className="mt-2.5 space-y-2.5" data-testid="agent-at-work-proofs">
        {deliverables.slice(0, 4).map((asset) => (
          <PlatformPreviewPanel key={asset.id} asset={asset} dark compact showStatus={false} />
        ))}
      </div>
    );
  }

  const blocks = [];
  if (focus?.text) {
    blocks.push(
      <div key="focus">
        <OutputLabel>{focus.label}</OutputLabel>
        <p className={cn("mt-1 whitespace-pre-wrap text-[12.5px] leading-relaxed text-[color:var(--text-2)]", stage.id === "research" && "font-mono text-[color:var(--accent-cyan)]")}>{focus.text}</p>
      </div>,
    );
  }
  if (sources.length > 0) {
    blocks.push(
      <div key="sources" className={focus?.text ? "mt-3 border-t pt-3" : ""} style={focus?.text ? { borderColor: "var(--border-subtle)" } : undefined}>
        <OutputLabel>Sources found · {sources.length}</OutputLabel>
        <div className="mt-2 space-y-2" data-testid="agent-at-work-sources">
          {sources.slice(0, 5).map((source, i) => {
            const c = confidenceMeta(source.confidence);
            return (
              <div key={`${source.url || source.title}-${i}`} className="grid grid-cols-[16px_minmax(0,1fr)_82px] items-center gap-2.5 text-[12px]">
                <span className="font-mono text-[color:var(--text-3)]">{i + 1}</span>
                <span className="min-w-0">
                  <span className="block truncate font-medium text-[color:var(--text-1)]">{source.title}</span>
                  <span className="block truncate font-mono text-[10.5px] text-[color:var(--accent-cyan)]/75">{source.domain || source.url || "captured source"}</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-[color:var(--surface-3)]">
                    <span className="block h-full rounded-full bg-[color:var(--accent-cyan)]" style={{ width: c.w }} />
                  </span>
                  <span className={cn("w-9 shrink-0 text-right text-[10px] font-semibold", c.cls)}>{source.confidence}</span>
                </span>
              </div>
            );
          })}
          {sources.length > 5 && <p className="pl-6 text-[11px] text-[color:var(--accent-cyan)]/80">+ {sources.length - 5} more in supporting materials.</p>}
        </div>
      </div>,
    );
  }
  // The real backend sub-phases that executed (with their notes).
  if (!focus?.text && !sources.length && subPhases.length > 0) {
    blocks.push(
      <div key="phases">
        <OutputLabel>Phases</OutputLabel>
        <div className="mt-2 space-y-1.5">
          {subPhases.map((p) => (
            <div key={p.id} className="min-w-0 text-[12px]">
              <span className="block truncate font-medium text-[color:var(--text-2)]">{p.label}</span>
              {p.note && <span className="block truncate text-[11px] text-[color:var(--text-3)]">{p.note}</span>}
            </div>
          ))}
        </div>
      </div>,
    );
  }

  if (!blocks.length) return null;
  return <OutputBox>{blocks}</OutputBox>;
}

function FeedStep({ stage, isLast, run, assets, deliverables }) {
  const running = stage.status === "running";
  const done = stage.status === "done" || stage.status === "warning";
  const dot = dotFor(stage.status);
  const revealed = stage.status !== "pending";

  return (
    <div className={cn("flex gap-3.5", !revealed && "opacity-55")} data-testid={`agent-step-${stage.id}`}>
      {/* rail */}
      <div className="flex flex-col items-center" style={{ width: 22 }}>
        <span
          className={cn(
            "relative z-[1] flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border-2 text-[11px] font-semibold transition-colors",
            dot.ring,
          )}
          style={{ backgroundColor: running ? "transparent" : undefined }}
        >
          {running ? (
            <>
              <span className="h-2 w-2 rounded-full bg-[color:var(--accent-cyan)]" />
              <span aria-hidden className="absolute inset-[-4px] rounded-full border-2 border-[color:var(--accent-cyan)]" style={{ animation: "aaw-ring 1.1s ease-out infinite" }} />
            </>
          ) : dot.glyph}
        </span>
        {!isLast && (
          <span
            className="my-1 w-0.5 flex-1"
            style={{ minHeight: 14, background: done ? "linear-gradient(var(--accent-green), var(--accent-cyan))" : "var(--border-subtle)" }}
          />
        )}
      </div>

      {/* body */}
      <div className="min-w-0 flex-1 pb-4">
        <div className="flex items-center gap-2">
          <span className={cn("text-[14px] font-medium", running || done ? "text-[color:var(--text-1)]" : "text-[color:var(--text-2)]")}>{stage.label}</span>
          {running && <ThinkingDots />}
          {stage.metric && revealed && stage.status !== "skipped" && (
            <span className="rounded-full border px-2 py-0.5 text-[10.5px] font-medium text-[color:var(--text-2)]" style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-3)" }}>{stage.metric}</span>
          )}
        </div>
        {stage.detail && revealed && (
          <p className="mt-1 text-[12px] leading-relaxed text-[color:var(--text-3)]">{stage.detail}</p>
        )}
        {revealed && <PhaseOutput stage={stage} run={run} assets={assets} deliverables={deliverables} />}
      </div>
    </div>
  );
}

export function AgentAtWork({ run, progress, deliverableCount = 0, supportingCount = 0, assets = [], deliverables = [], className, action }) {
  const stages = useMemo(
    () => buildMissionControlStages(run, { deliverableCount, supportingCount }),
    [run, deliverableCount, supportingCount],
  );

  const done = run?.status === "completed";
  const failed = run?.status === "failed";
  const running = !done && !failed;

  // Live elapsed timer while running.
  const startedAt = useMemo(() => {
    const t = new Date(run?.started_at || run?.created_at || Date.now()).getTime();
    return Number.isFinite(t) ? t : Date.now();
  }, [run?.started_at, run?.created_at]);
  const [now, setNow] = useState(() => Date.now());
  const raf = useRef();
  useEffect(() => {
    if (!running) return undefined;
    let alive = true;
    const tick = () => { if (!alive) return; setNow(Date.now()); raf.current = window.setTimeout(tick, 120); };
    tick();
    return () => { alive = false; window.clearTimeout(raf.current); };
  }, [running]);

  if (!run) return null;

  // Prefer wall-clock while running, but fall back to summed phase durations when
  // the run's started_at is stale/absent (so the timer never shows nonsense).
  const stepMs = (run.steps || []).reduce((sum, st) => sum + (Number(st.duration_ms) || 0), 0);
  const liveMs = now - startedAt;
  const shownMs = done
    ? (run.duration_ms || stepMs)
    : (liveMs < 0 || liveMs > 6 * 3600 * 1000 ? stepMs : liveMs);

  const activeStage = stages.find((s) => s.status === "running");
  const statusLine = failed
    ? (run.error || "The run stopped before drafts were saved.")
    : done
      ? `Done — ${deliverableCount || deliverables.length || "your"} ${(deliverableCount || deliverables.length) === 1 ? "proof" : "proofs"} ready for review.`
      : (activeStage?.detail || "Starting…");

  return (
    <section
      className={cn("overflow-hidden rounded-2xl border", className)}
      style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-1)" }}
      data-testid="agent-at-work"
    >
      <style>{`
        @keyframes aaw-breathe { 0%,100% { box-shadow:0 0 0 0 rgba(143, 224, 74,0.45); transform:scale(1);} 50% { box-shadow:0 0 0 7px rgba(143, 224, 74,0); transform:scale(1.06);} }
        @keyframes aaw-ring { 0% { transform:scale(0.8); opacity:.5;} 100% { transform:scale(1.6); opacity:0;} }
        @keyframes aaw-blink { 0%,100% { opacity:.25;} 50% { opacity:1;} }
        @media (prefers-reduced-motion: reduce){ [data-testid="agent-at-work"] * { animation: none !important; } }
      `}</style>

      {/* header */}
      <div className="flex items-center gap-3 border-b px-4 py-3.5" style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-2)" }}>
        <span
          className="h-[34px] w-[34px] shrink-0 rounded-full"
          aria-hidden
          style={{
            background: done
              ? "radial-gradient(circle at 35% 30%, #8FE0AB, #2E9E5C)"
              : failed
                ? "radial-gradient(circle at 35% 30%, #F3B0AA, #C15A50)"
                : "radial-gradient(circle at 35% 30%, #9BF3FF, #17B6C9)",
            animation: running ? "aaw-breathe 2s ease-in-out infinite" : undefined,
          }}
        />
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold text-[color:var(--text-1)]">Content Engine</p>
          <p className={cn("truncate text-[12.5px]", done ? "text-[color:var(--accent-green)]" : failed ? "text-[color:var(--accent-red)]" : "text-[color:var(--accent-cyan)]")}>{statusLine}</p>
        </div>
        {action}
        <span className="shrink-0 font-mono text-[12px] text-[color:var(--text-3)]" data-testid="agent-at-work-timer">
          {elapsedLabel(shownMs)}
        </span>
      </div>

      {/* feed */}
      <div className="px-4 pb-3 pt-3">
        {stages.map((stage, i) => (
          <FeedStep key={stage.id} stage={stage} isLast={i === stages.length - 1} run={run} assets={assets} deliverables={deliverables} />
        ))}

        {done && (
          <div className="ml-[34px] rounded-lg border border-dashed px-3 py-2.5 text-[12px] text-[color:var(--text-3)]" style={{ borderColor: "var(--border-subtle)" }}>
            Nothing is approved yet — review, edit, or regenerate these below.
          </div>
        )}
      </div>
    </section>
  );
}
